import {
  type ButtonHTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from "react"
import {
  Button,
  type ButtonProps,
} from "@/components/resources/components/ui/Button"
import { subscribeToNewsLetter } from "@/components/resources/data/api/newsletter"

type Status = "sending" | "success" | "fail" | ""
type StatusHandler = (s: Status) => void
interface Props {
  onStatusChange?: StatusHandler
  noFeedback?: boolean
}

const getFeedback = (status?: Status) => {
  switch (status) {
    case "fail":
      return (
        <span className="text-red-500 font-medium">Failed to subscribe.</span>
      )
    case "success":
      return (
        <span className="text-green-500 font-medium">
          Thank you for subscribing!
        </span>
      )
    default:
      return null
  }
}

let updateStatusTimeout: undefined | NodeJS.Timeout

export const NewsletterSubscribe = ({
  onClick,
  onStatusChange,
  noFeedback,
  ...props
}: Omit<ButtonProps, "children"> & Props) => {
  const [email, setEmail] = useState("")
  const [postStatus, setPostStatus] = useState<Status>("")

  const updateStatusHandler = useCallback(
    (s: Status) => {
      setPostStatus(s)
      onStatusChange?.(s)
    },
    [onStatusChange]
  )

  const isDisabled =
    !(email.includes("@") && email.length > 2) || postStatus === "sending"

  const subscribe = useCallback<
    NonNullable<ButtonHTMLAttributes<HTMLButtonElement>["onClick"]>
  >(
    async (e) => {
      e.preventDefault()

      if (!isDisabled) {
        try {
          updateStatusHandler("sending")
          // gaFormEvent({ type: "submit", name: "newsletter_signup" })
          await subscribeToNewsLetter(email)
          // gaFormEvent({ type: "success", name: "newsletter_signup" })
          // await new Promise<void>((res, rej) => { setTimeout(() => {res()}, 500)})
          updateStatusHandler("success")
          setEmail("")
          updateStatusTimeout = setTimeout(() => {
            updateStatusHandler("")
          }, 4000)
        } catch (error) {
          console.error("Subscribing to newsletter failed", error)
          // Sentry.captureException(error)
          updateStatusHandler("fail")
          updateStatusTimeout = setTimeout(() => {
            updateStatusHandler("")
          }, 3000)
        }
        onClick?.(e)
      }
    },
    [isDisabled, email, onClick, updateStatusHandler]
  )

  useEffect(() => {
    return () => {
      if (updateStatusTimeout) {
        clearTimeout(updateStatusTimeout)
      }
    }
  }, [])

  const feedback = noFeedback ? null : getFeedback(postStatus)

  return (
    <>
      <label className="sr-only">Email</label>
      <input
        type="email"
        placeholder="Your email address"
        required
        onChange={({ target: { value } }) => {
          setEmail(value)
        }}
        value={email}
        className="px-3.5 py-2 rounded-lg text-sm bg-neutral-900/4 dark:bg-neutral-300/9 border border-solid border-border-default text-fg-default placeholder:text-fg-secondary dark:placeholder:text-neutral-600 focus:outline-hidden focus:border-neutral-300 dark:focus:border-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
      />
      <Button
        {...props}
        onClick={subscribe}
        disabled={isDisabled}
        variant="solid"
      >
        {postStatus === "sending" ? "Signing up..." : "Sign up"}
      </Button>
      {feedback}
    </>
  )
}
